import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Radio,
  Typography,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Card,
  TextField,
  Select,
  MenuItem,
  Stack,
  Divider,
} from '@mui/material';
import { getJwtTokenFromIdToken } from '../auth/AuthFanc';
import { fetchErrorText } from '../../../common/const';
import { useNavigate } from 'react-router-dom';
import { Error } from '@mui/icons-material';

const SNACKBAR_TEXT_REGISTER_SUCCESS = 'OTAの実行を予約しました。';
const SNACKBAR_TEXT_REGISTER_SUCCESS_NOW = 'OTAの実行を開始しました。';

/**
 * `2024-07-25T02:44:55.266Z` -> `2024-07-25`
 */
const dateToLocaleDateStr = (date: Date): string => {
  return (
    String(date.getFullYear()).padStart(4, '0') +
    '-' +
    String(date.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(date.getDate()).padStart(2, '0')
  );
};

/**
 * Date object to initialize schedule; set to one hour later from now.
 */
const defaultScheduleDate = (): Date => {
  const d = new Date();
  d.setHours(d.getHours() + 1);
  return d;
};

/**
 * Gives 'HH:MM' formatted string from a hours value (minutes part is always
 * '00').
 */
const hoursToHoursMinutesStr = (hours: number): string => {
  return String(hours).padStart(2, '0') + ':00';
};

/**
 * Returns a Date object from a Date object and a 'HH:MM' formatted string,
 * setting its minutes, seconds and milliseconds to 0.
 */
const mergeDateAndHours = (date: Date, hours: string): Date => {
  const datetime = date;
  datetime.setHours(Number(hours.substring(0, 2)));
  datetime.setMinutes(0);
  datetime.setSeconds(0);
  datetime.setMilliseconds(0);
  return datetime;
};

interface Firmware {
  autoFirmwareBinaryFileId: number;
  fileName: string;
}

interface Sdc {
  sdcUuid: string;
  serialNumber: string;
}

interface NewOtaModalProps {
  open: boolean;
  close: () => void;
  firmware: Firmware;
  sdcs: Sdc[];
  openSnackBarWithError: (text: string) => void;
}

interface ConflictSdcListDialogProps {
  close: () => void;
  sdcs: Sdc[];
}

const ConflictSdcListDialog: React.FC<ConflictSdcListDialogProps> = (
  props: ConflictSdcListDialogProps
) => {
  return (
    <Dialog
      open={props.sdcs.length > 0}
      onClose={props.close}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle color="error">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Error sx={{ mr: 1 }} />
          エラー
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 1 }}>
          すでに付近の時刻にOTA予定があるSDCが含まれています。
        </Typography>
        {props.sdcs.map((sdc, i) => {
          return (
            <Box key={`sdcs-${i}`}>
              <Box sx={{ display: 'flex' }}>
                <Stack direction="column" textAlign="right" sx={{ mr: 1 }}>
                  <Typography variant="body2">シリアルナンバー：</Typography>
                  <Typography variant="body2">UUID：</Typography>
                </Stack>
                <Stack direction="column" textAlign="left">
                  <Typography variant="body2">{sdc.serialNumber}</Typography>
                  <Typography variant="body2">{sdc.sdcUuid}</Typography>
                </Stack>
              </Box>
              {i !== props.sdcs.length - 1 && <Divider sx={{ my: 1 }} />}
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={props.close} color="primary">
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const NewOtaModal: React.FC<NewOtaModalProps> = (props: NewOtaModalProps) => {
  const navigate = useNavigate();

  const [conductNow, setConductNow] = React.useState<boolean>(false);
  const [scheduledAtDate, setScheduledAtDate] =
    React.useState<Date>(defaultScheduleDate);
  const [scheduledAtHours, setScheduledAtHours] = React.useState<string>(
    // generate 'HH:MM' like string from default Date (minutes part is always '00')
    hoursToHoursMinutesStr(defaultScheduleDate().getHours())
  );

  const [validationErrors, setValidationErrors] = React.useState({
    scheduledAt: '',
  });

  const [isSubmitInProgress, setIsSubmitInProgress] =
    React.useState<boolean>(false);

  // filled when handleSubmit result was 409 Conflict
  const [conflictSdcs, setConflictSdcs] = React.useState<Sdc[]>([]);

  const handleValidation = () => {
    const currentDateTs = new Date().getTime();
    const scheduledAtDatetime = mergeDateAndHours(
      scheduledAtDate,
      scheduledAtHours
    );
    const errors = {
      scheduledAt:
        !conductNow &&
        scheduledAtDatetime.getTime() < currentDateTs + 5 * 60 * 1000
          ? `現在時刻の5分後以降のみ指定できます。`
          : '',
    };
    setValidationErrors(errors);
    return !Object.values(errors).some((error) => error !== '');
  };

  const handleClose = () => {
    // reset useState variables to reset the view on next time opened
    setValidationErrors({
      scheduledAt: '',
    });
    setConductNow(false);
    setScheduledAtDate(defaultScheduleDate);
    setScheduledAtHours(
      hoursToHoursMinutesStr(defaultScheduleDate().getHours())
    );

    // close this modal
    props.close();
  };

  const handleSelectConductNow = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConductNow(e.target.value === 'true');
  };

  const handleSubmit = async () => {
    // check validation result (applies to helper texts), return if NG
    if (!handleValidation()) {
      return;
    }

    setIsSubmitInProgress(true);

    await getJwtTokenFromIdToken()
      .then((token) => {
        if (conductNow) {
          // call conductFirmwareUpdateNow API
          return fetch(
            `${process.env.REACT_APP_API_ENDPOINT}/firmware-updates/now`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: token,
              },
              body: JSON.stringify({
                autoFirmwareBinaryFileId:
                  props.firmware.autoFirmwareBinaryFileId,
                sdcUuids: props.sdcs.map((sdc) => {
                  return sdc.sdcUuid;
                }),
              }),
            }
          );
        } else {
          const scheduledAtDatetime = mergeDateAndHours(
            scheduledAtDate,
            scheduledAtHours
          );
          // call storeFirmwareUpdateEvent API
          return fetch(
            `${process.env.REACT_APP_API_ENDPOINT}/firmware-updates`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: token,
              },
              body: JSON.stringify({
                autoFirmwareBinaryFileId:
                  props.firmware.autoFirmwareBinaryFileId,
                scheduledAt: scheduledAtDatetime.toISOString(),
                sdcUuids: props.sdcs.map((sdc) => {
                  return sdc.sdcUuid;
                }),
              }),
            }
          );
        }
      })
      .then((response) => {
        if (response.status === 409) {
          // conflict: Some of props.sdcs have already been reserved for OTA
          // at the time.
          response.json().then((body) => {
            setConflictSdcs(
              body.conflictedSdcs.map((sdc: any) => {
                return {
                  sdcUuid: sdc.sdcUuid,
                  serialNumber: sdc.sdcSerialNumber,
                };
              })
            );
          });
          return null;
        } else if (!response.ok) {
          console.error(`HTTP error! status: ${response.status}`);
          props.openSnackBarWithError(fetchErrorText);
          return null;
        }
        return response.json();
      })
      .then((body) => {
        if (body !== null) {
          // show confirm dialog and go to scheduled OTA list page
          window.alert(
            conductNow
              ? SNACKBAR_TEXT_REGISTER_SUCCESS_NOW
              : SNACKBAR_TEXT_REGISTER_SUCCESS
          );
          navigate('/ota-schedules');
        }
      })
      .catch((e) => {
        props.openSnackBarWithError(fetchErrorText);
        console.error(e);
      })
      .finally(() => {
        setIsSubmitInProgress(false);
      });
  };

  React.useEffect(() => {
    // call handleValidation to update helperText
    handleValidation();
  }, [conductNow, scheduledAtDate, scheduledAtHours]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Dialog
        open={props.open}
        onClose={() => handleClose()}
        aria-labelledby="form-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="form-dialog-title">新規OTA実行予約</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1">対象SDC</Typography>
            <Card variant="outlined" sx={{ ml: 2, mb: 0.5, p: 1 }}>
              <Typography variant="body2" fontFamily="monospace">
                {props.sdcs
                  .map((sdc) => {
                    return sdc.serialNumber;
                  })
                  .join(', ')}
              </Typography>
            </Card>
            <Typography
              variant="body2"
              sx={{ ml: 2 }}
            >{`（計 ${props.sdcs.length}）`}</Typography>
          </Box>
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1">対象ファームウェア</Typography>
            <Card variant="outlined" sx={{ ml: 2, p: 1 }}>
              <Typography variant="body2" fontFamily="monospace">
                {props.firmware.fileName}
              </Typography>
            </Card>
          </Box>
          <Box>
            <Typography variant="subtitle1">実行日時</Typography>
            <FormControl sx={{ ml: 2 }}>
              <RadioGroup
                value={String(conductNow)}
                onChange={handleSelectConductNow}
              >
                <FormControlLabel
                  value="false"
                  control={<Radio size="small" />}
                  label="日時を指定する"
                  sx={{ mb: -1 }}
                />
                <Box sx={{ ml: 4, display: 'flex', alignItems: 'top' }}>
                  <TextField
                    margin="dense"
                    size="small"
                    id="scheduledAt"
                    type="date"
                    value={dateToLocaleDateStr(scheduledAtDate)}
                    onChange={(e) => {
                      setScheduledAtDate(new Date(e.target.value));
                    }}
                    disabled={conductNow}
                    error={!!validationErrors.scheduledAt}
                    helperText={validationErrors.scheduledAt}
                    sx={{ mr: 1 }}
                  />
                  <FormControl margin="dense" size="small">
                    <Select
                      value={scheduledAtHours}
                      onChange={(e) => {
                        setScheduledAtHours(e.target.value);
                      }}
                      disabled={conductNow}
                      error={!!validationErrors.scheduledAt}
                    >
                      {Array.from(Array(24).keys()).map((i) => {
                        const hoursStr = hoursToHoursMinutesStr(i);
                        return (
                          <MenuItem value={hoursStr} key={`hours-${i}`}>
                            {hoursStr}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box>
                <FormControlLabel
                  value="true"
                  control={<Radio size="small" />}
                  label="今すぐに実行する"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleClose()} color="primary">
            キャンセル
          </Button>
          <Button onClick={handleSubmit} color="primary">
            {isSubmitInProgress ? <CircularProgress size={20} /> : '登録'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConflictSdcListDialog
        close={() => {
          setConflictSdcs([]);
        }}
        sdcs={conflictSdcs}
      />
    </>
  );
};

export default NewOtaModal;
